/**
 * FeatureErrorBoundary.jsx
 * Feature-specific error boundaries with contextual fallback UIs
 *
 * Provides better error isolation by wrapping major features with their own
 * error boundaries, preventing one feature crash from taking down the entire app.
 */

import React from 'react';
import PropTypes from 'prop-types';
import ErrorBoundary from './ErrorBoundary';
import { AlertTriangle, RefreshCw, Home, FileText, BarChart3, TestTube, Users, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Base feature error fallback component
 */
const FeatureErrorFallback = ({
  featureName,
  icon: Icon,
  error,
  onRetry,
  homeRoute = '/',
  description
}) => {
  const navigate = useNavigate();

  const isChunkError = error?.message?.includes('Loading chunk') ||
                       error?.message?.includes('Failed to fetch') ||
                       error?.name === 'ChunkLoadError';

  const errorMessage = isChunkError
    ? `Failed to load ${featureName}. This might be due to a network issue or an app update.`
    : `An error occurred in ${featureName}.`;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      padding: '40px 20px',
      textAlign: 'center'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: '#fef2f2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '24px'
      }}>
        <Icon size={40} color="#dc2626" />
      </div>

      <h2 style={{
        fontSize: '24px',
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '12px'
      }}>
        {featureName} Unavailable
      </h2>

      <p style={{
        fontSize: '16px',
        color: '#6b7280',
        maxWidth: '500px',
        marginBottom: '8px'
      }}>
        {errorMessage}
      </p>

      {description && (
        <p style={{
          fontSize: '14px',
          color: '#9ca3af',
          maxWidth: '500px',
          marginBottom: '24px'
        }}>
          {description}
        </p>
      )}

      <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
        <button
          onClick={onRetry}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = '#b91c1c'}
          onMouseOut={(e) => e.target.style.background = '#dc2626'}
        >
          <RefreshCw size={18} />
          Try Again
        </button>

        {homeRoute && (
          <button
            onClick={() => navigate(homeRoute)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '500',
              background: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#e5e7eb'}
            onMouseOut={(e) => e.target.style.background = '#f3f4f6'}
          >
            <Home size={18} />
            Go to {homeRoute === '/' ? 'Login' : 'Dashboard'}
          </button>
        )}
      </div>

      {process.env.NODE_ENV === 'development' && error && (
        <details style={{
          marginTop: '32px',
          padding: '16px',
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          textAlign: 'left',
          maxWidth: '600px',
          width: '100%'
        }}>
          <summary style={{
            cursor: 'pointer',
            fontWeight: '500',
            color: '#6b7280',
            marginBottom: '8px'
          }}>
            Error Details (Development Only)
          </summary>
          <pre style={{
            fontSize: '12px',
            color: '#dc2626',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {error.toString()}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        </details>
      )}
    </div>
  );
};

FeatureErrorFallback.propTypes = {
  featureName: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  error: PropTypes.object,
  onRetry: PropTypes.func.isRequired,
  homeRoute: PropTypes.string,
  description: PropTypes.string
};

/**
 * Dashboard Error Boundary
 */
export const DashboardErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="Dashboard"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Dashboard"
        icon={BarChart3}
        error={error}
        onRetry={retry}
        homeRoute="/dashboard"
        description="You can return to the main view or try reloading the dashboard."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

DashboardErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Automation Scripts Error Boundary
 */
export const AutomationScriptsErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="AutomationScripts"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Automation Scripts"
        icon={TestTube}
        error={error}
        onRetry={retry}
        homeRoute="/dashboard"
        description="Your automation scripts are temporarily unavailable. Other features remain accessible."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

AutomationScriptsErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Quality Intelligence / Data Analyzer Error Boundary
 */
export const QualityIntelligenceErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="QualityIntelligence"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Quality Intelligence"
        icon={FileText}
        error={error}
        onRetry={retry}
        homeRoute="/dashboard"
        description="The AI analysis feature is temporarily unavailable. Your data is safe."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

QualityIntelligenceErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Project Generation Error Boundary
 */
export const ProjectGenerationErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="ProjectGeneration"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Project Generation"
        icon={FileText}
        error={error}
        onRetry={retry}
        homeRoute="/dashboard"
        description="The project generation AI is temporarily unavailable. Try again in a moment."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

ProjectGenerationErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Organization Management Error Boundary
 */
export const OrganizationErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="Organization"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Organization Settings"
        icon={Users}
        error={error}
        onRetry={retry}
        homeRoute="/dashboard"
        description="Organization settings are temporarily unavailable. Other features remain functional."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

OrganizationErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Token Usage Error Boundary
 */
export const TokenUsageErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="TokenUsage"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Token Usage Analytics"
        icon={DollarSign}
        error={error}
        onRetry={retry}
        homeRoute="/dashboard"
        description="Token usage analytics are temporarily unavailable. Usage tracking continues normally."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

TokenUsageErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Projects Error Boundary
 */
export const ProjectsErrorBoundary = ({ children }) => (
  <ErrorBoundary
    componentName="Projects"
    fallback={({ error, retry }) => (
      <FeatureErrorFallback
        featureName="Projects"
        icon={FileText}
        error={error}
        onRetry={retry}
        homeRoute="/dashboard"
        description="Project management is temporarily unavailable. Your projects are safe."
      />
    )}
  >
    {children}
  </ErrorBoundary>
);

ProjectsErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Re-export the base ErrorBoundary for custom use cases
 */
export { ErrorBoundary };

/**
 * Default export - base ErrorBoundary
 */
export default ErrorBoundary;
